import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { creerApi, ErreurApi, type Canal, type Espace, type Membre, type Message } from './api.js';
import { choisirFournisseur, type Session } from './auth.js';
import { apercu, composer, RendreCorps, type Doc } from './richtext.js';

/**
 * Dernier espace et dernier canal ouverts.
 *
 * Sans cette mémoire, rouvrir l'onglet renvoie sur le premier espace par
 * ordre alphabétique — ce qui, pour quelqu'un qui appartient à plusieurs
 * espaces, veut dire « pas le sien » une fois sur deux.
 */
const memoire = {
  lire(cle: string): string | null {
    try { return localStorage.getItem('zephyr.' + cle); } catch { return null; }
  },
  ecrire(cle: string, valeur: string): void {
    try { localStorage.setItem('zephyr.' + cle, valeur); } catch { /* stockage refusé */ }
  },
};

/** Temporisation entièrement aléatoire — voir packages/client. */
function attenteAvecAlea(tentative: number, base = 500, plafond = 30_000): number {
  return Math.floor(Math.random() * Math.min(plafond, base * 2 ** Math.min(tentative, 8)));
}

const jetonEnvoi = () =>
  globalThis.crypto?.randomUUID?.() ?? Date.now().toString(36) + Math.random().toString(36).slice(2);

type Etat = 'connexion' | 'pret' | 'coupe';

export default function App() {
  const fournisseur = useMemo(choisirFournisseur, []);
  const [session, setSession] = useState<Session | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const api = useMemo(() => creerApi(() => session?.jeton ?? null), [session]);

  const [moi, setMoi] = useState<{ id: string; display_name: string; avatar_color: string } | null>(null);
  const [espaces, setEspaces] = useState<Espace[]>([]);
  const [espace, setEspace] = useState<string | null>(null);
  const [canaux, setCanaux] = useState<Canal[]>([]);
  const [canal, setCanal] = useState<string | null>(null);
  const [membres, setMembres] = useState<Membre[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [etat, setEtat] = useState<Etat>('connexion');
  const [saisie, setSaisie] = useState<string>('');
  const [ecrivent, setEcrivent] = useState<Record<string, string>>({});
  const [presences, setPresences] = useState<Record<string, string>>({});
  /** Fil ouvert : identifiant du message racine, ou null. */
  const [fil, setFil] = useState<string | null>(null);
  const [reponses, setReponses] = useState<Message[]>([]);
  const [saisieFil, setSaisieFil] = useState('');

  const socket = useRef<Socket | null>(null);
  const tentative = useRef(0);
  const canalRef = useRef<string | null>(null);
  const filRef = useRef<string | null>(null);
  const finDuFil = useRef<HTMLDivElement | null>(null);
  canalRef.current = canal;
  filRef.current = fil;

  // ── Connexion ──────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const s = (await fournisseur.terminer()) ?? fournisseur.session();
        if (s) setSession(s);
        else await fournisseur.connecter();
      } catch (e) {
        setErreur((e as Error).message);
      }
    })();
  }, [fournisseur]);

  useEffect(() => {
    if (!session) return;
    (async () => {
      try {
        const [{ user }, { workspaces }] = await Promise.all([api.moi(), api.espaces()]);
        setMoi(user);
        setEspaces(workspaces);
        if (workspaces.length > 0) {
          const dernier = memoire.lire('espace');
          const retrouve = workspaces.find((w) => w.id === dernier);
          setEspace((retrouve ?? workspaces[0]!).id);
        } else {
          // Premier passage : on ne montre pas une interface vide sans
          // expliquer quoi faire.
          const { workspace } = await api.creerEspace('principal', 'Mon espace');
          setEspaces([workspace]);
          setEspace(workspace.id);
        }
      } catch (e) {
        setErreur(messageDe(e));
      }
    })();
  }, [session, api]);

  // ── Chargement d'un espace ─────────────────────────────────
  const rafraichirCanaux = useCallback(async () => {
    try {
      const { channels } = await api.canaux();
      setCanaux(channels);
      return channels;
    } catch (e) {
      setErreur(messageDe(e));
      return [];
    }
  }, [api]);

  useEffect(() => {
    if (!espace) return;
    api.definirEspace(espace);
    memoire.ecrire('espace', espace);
    (async () => {
      const channels = await rafraichirCanaux();
      const { members } = await api.membres(espace).catch(() => ({ members: [] as Membre[] }));
      setMembres(members);
      const dernier = memoire.lire('canal:' + espace);
      const retrouve = channels.find((c) => c.id === dernier);
      const choisi = retrouve ?? channels.find((c) => c.joined) ?? channels[0];
      if (choisi) setCanal(choisi.id);
    })();
  }, [espace, api, rafraichirCanaux]);

  // ── Fil du canal ───────────────────────────────────────────
  useEffect(() => {
    if (!canal) return;
    if (espace) memoire.ecrire('canal:' + espace, canal);
    (async () => {
      try {
        const { messages: m } = await api.messages(canal);
        setMessages(m);
        await api.marquerLu(canal).catch(() => undefined);
        await api.acquitter(canal).catch(() => undefined);
        void rafraichirCanaux();
      } catch (e) {
        setErreur(messageDe(e));
      }
    })();
  }, [canal, espace, api, rafraichirCanaux]);

  useEffect(() => {
    finDuFil.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  // ── Fil de discussion ──────────────────────────────────────
  useEffect(() => {
    if (!fil || !canal) { setReponses([]); return; }
    (async () => {
      try {
        const { messages: r } = await api.messages(canal, { thread: fil });
        setReponses(r);
      } catch (e) {
        setErreur(messageDe(e));
      }
    })();
  }, [fil, canal, api]);

  // ── Temps réel ─────────────────────────────────────────────
  useEffect(() => {
    if (!session || !espace) return;
    let vivant = true;

    const ouvrir = () => {
      const s = io({
        auth: { token: session.jeton, workspace_id: espace },
        transports: ['websocket', 'polling'],
        // Reconnexion pilotée à la main pour y mettre un vrai aléa :
        // celle de Socket.IO fait repartir tous les clients ensemble.
        reconnection: false,
      });
      socket.current = s;

      s.on('connection:state', () => { setEtat('pret'); tentative.current = 0; });
      s.on('disconnect', () => { setEtat('coupe'); reprogrammer(); });
      s.on('connect_error', () => { setEtat('coupe'); reprogrammer(); });

      s.on('message:new', (m: Message) => {
        if (m.channel_id !== canalRef.current) { void rafraichirCanaux(); return; }
        if (m.parent_id) {
          // Une réponse ne remonte pas dans le canal : elle va dans son
          // fil, et le compteur de la racine avance. C'est toute la
          // raison d'être des fils — sans cela, le canal redevient bavard.
          setMessages((liste) => liste.map((x) =>
            x.id === m.parent_id ? { ...x, reply_count: (x.reply_count ?? 0) + 1 } : x));
          if (m.parent_id === filRef.current) {
            setReponses((liste) => (liste.some((x) => x.id === m.id) ? liste : [...liste, m]));
          }
          return;
        }
        setMessages((liste) => (liste.some((x) => x.id === m.id) ? liste : [...liste, m]));
      });
      s.on('message:deleted', (d: { id: string }) =>
        setMessages((liste) => liste.filter((m) => m.id !== d.id)));
      s.on('reaction:changed', (r: { message_id: string; emoji: string; count: number; user_id: string; added: boolean }) =>
        setMessages((liste) => liste.map((m) => (m.id === r.message_id ? appliquerReaction(m, r, moi?.id) : m))));
      s.on('typing:started', (t: { user_id: string; display_name: string; channel_id: string }) => {
        if (t.channel_id !== canalRef.current) return;
        setEcrivent((e) => ({ ...e, [t.user_id]: t.display_name }));
        setTimeout(() => setEcrivent((e) => {
          const { [t.user_id]: _, ...reste } = e;
          return reste;
        }), 5000);
      });
      s.on('presence:changed', (p: { user_id: string; status: string }) =>
        setPresences((x) => ({ ...x, [p.user_id]: p.status })));
      s.on('notification:new', () => void rafraichirCanaux());

      const battement = setInterval(() => s.emit('presence:ping'), 15_000);
      s.on('disconnect', () => clearInterval(battement));
    };

    const reprogrammer = () => {
      if (!vivant) return;
      tentative.current += 1;
      setTimeout(() => { if (vivant) ouvrir(); }, attenteAvecAlea(tentative.current));
    };

    ouvrir();
    return () => { vivant = false; socket.current?.close(); socket.current = null; };
  }, [session, espace, moi?.id, rafraichirCanaux]);

  // ── Envoi ──────────────────────────────────────────────────
  const envoyer = useCallback(async (dansLeFil = false) => {
    const texte = (dansLeFil ? saisieFil : saisie).trim();
    if (!texte || !canal || !moi) return;
    if (dansLeFil && !fil) return;
    const doc = composer(texte, membres);
    const token = jetonEnvoi();
    const parent = dansLeFil ? fil! : undefined;

    // Affiché immédiatement, marqué « en cours ». L'accusé le remplace ;
    // s'il n'arrive pas, la marque reste — jamais de message qui disparaît
    // en silence.
    const provisoire: Message = {
      id: 'local:' + token, channel_id: canal, parent_id: parent ?? null,
      author_id: moi.id, display_name: moi.display_name, avatar_color: moi.avatar_color,
      body: doc, created_at: new Date().toISOString(), edited_at: null,
      en_cours: true, client_token: token,
    };
    if (dansLeFil) { setSaisieFil(''); setReponses((l) => [...l, provisoire]); }
    else { setSaisie(''); setMessages((l) => [...l, provisoire]); }

    const remplacer = (reel: Message) => {
      const majuscule = (l: Message[]) =>
        l.map((m) => (m.client_token === token ? { ...reel, en_cours: false } : m));
      if (dansLeFil) {
        setReponses(majuscule);
        setMessages((l) => l.map((m) =>
          m.id === parent ? { ...m, reply_count: (m.reply_count ?? 0) + 1 } : m));
      } else setMessages(majuscule);
    };

    const s = socket.current;
    if (s?.connected) {
      s.emit('message:send', { channel_id: canal, body: doc, parent_id: parent, client_token: token },
        (r: { ok: boolean; data?: Message; message?: string }) => {
          if (r?.ok && r.data) remplacer(r.data);
          else setErreur(r?.message ?? 'L’envoi a échoué.');
        });
      return;
    }
    // Socket coupée : on passe par HTTP plutôt que d'attendre le retour du
    // temps réel. Le message part, c'est ce qui compte.
    try {
      const { message } = await api.envoyer(canal, doc, parent);
      remplacer(message);
    } catch (e) {
      setErreur(messageDe(e));
    }
  }, [saisie, saisieFil, canal, fil, moi, membres, api]);

  const reagir = useCallback(async (message: string, emoji: string) => {
    const s = socket.current;
    if (s?.connected) s.emit('reaction:toggle', { message_id: message, emoji });
    else await api.reagir(message, emoji).catch((e) => setErreur(messageDe(e)));
    setMessages((l) => l.map((m) => (m.id === message ? basculerReaction(m, emoji) : m)));
  }, [api]);

  // ── Rendu ──────────────────────────────────────────────────
  if (!session) {
    return (
      <div className="accueil">
        <Marque />
        <p>{erreur ?? 'Connexion en cours…'}</p>
      </div>
    );
  }

  const courant = canaux.find((c) => c.id === canal);

  return (
    <div className="app">
      <header className="topbar">
        <Marque />
        <span className={'etat ' + etat}>
          {etat === 'pret' ? 'En ligne' : etat === 'coupe' ? 'Reconnexion…' : 'Connexion…'}
        </span>
        {moi && (
          <span className="av" style={{ background: moi.avatar_color }} title={moi.display_name}>
            {initiales(moi.display_name)}
          </span>
        )}
      </header>

      {erreur && (
        <div className="bandeau-erreur" role="alert">
          {erreur}
          <button onClick={() => setErreur(null)} aria-label="Fermer">×</button>
        </div>
      )}

      <aside className="sidebar">
        <div className="ws-head">
          <select
            value={espace ?? ''}
            onChange={(e) => setEspace(e.target.value)}
            aria-label="Espace"
            data-testid="espace"
          >
            {espaces.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <nav className="nav" aria-label="Canaux">
          <div className="grp-h">Canaux</div>
          {canaux.map((c) => (
            <button
              key={c.id}
              className={'item' + (c.id === canal ? ' actif' : '') + (c.unread > 0 ? ' non-lu' : '')}
              onClick={() => setCanal(c.id)}
              aria-current={c.id === canal}
              data-testid="canal"
              data-canal={c.name}
            >
              <span className="hash">#</span>
              <span className="label">{c.name}</span>
              {c.mentions > 0 && <span className="pastille">{c.mentions}</span>}
              {c.mentions === 0 && c.unread > 0 && <span className="point" aria-label="non lus" />}
            </button>
          ))}
        </nav>
      </aside>

      <main className="main">
        <div className="ch-head">
          <h2>{courant ? '# ' + courant.name : ''}</h2>
          {courant?.topic && <span className="topic">{courant.topic}</span>}
        </div>

        <div className="stream" data-testid="fil">
          {messages.length === 0 && <p className="vide">Aucun message. Lancez la conversation.</p>}
          {messages.map((m, i) => {
            const groupe = i > 0 && messages[i - 1]!.author_id === m.author_id;
            return (
              <article
                key={m.id}
                className={'msg' + (groupe ? ' groupe' : '') + (m.en_cours ? ' en-cours' : '')}
                data-testid="message"
              >
                {!groupe && (
                  <span className="av" style={{ background: m.avatar_color ?? '#8A8A93' }}>
                    {initiales(m.display_name ?? '?')}
                  </span>
                )}
                <div className="corps">
                  {!groupe && (
                    <div className="mhead">
                      <b>{m.display_name ?? 'Compte supprimé'}</b>
                      <time>{heure(m.created_at)}</time>
                      {m.en_cours && <span className="en-cours-note">envoi…</span>}
                    </div>
                  )}
                  <div className="texte">
                    <RendreCorps doc={m.body} moi={moi?.id} />
                  </div>
                  <div className="reacts">
                    {(m.reactions ?? []).map((r) => (
                      <button
                        key={r.emoji}
                        className={'react' + (r.me ? ' mienne' : '')}
                        onClick={() => reagir(m.id, r.emoji)}
                      >
                        {r.emoji} {r.count}
                      </button>
                    ))}
                    {!m.en_cours && (
                      <button
                        className="react ajout"
                        data-testid="reagir"
                        onClick={() => reagir(m.id, '👍')}
                        title="Réagir"
                      >
                        ＋
                      </button>
                    )}
                    {!m.en_cours && (
                      <button
                        className="react ajout"
                        data-testid="repondre"
                        onClick={() => setFil(m.id)}
                        title="Répondre dans un fil"
                      >
                        Répondre
                      </button>
                    )}
                  </div>
                  {(m.reply_count ?? 0) > 0 && (
                    <button className="lien-fil" data-testid="ouvrir-fil" onClick={() => setFil(m.id)}>
                      {m.reply_count} réponse{(m.reply_count ?? 0) > 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
          <div ref={finDuFil} />
        </div>

        <div className="composer-wrap">
          <div className="ecrivent">
            {Object.values(ecrivent).length > 0 &&
              `${Object.values(ecrivent).join(', ')} ${Object.values(ecrivent).length > 1 ? 'écrivent' : 'écrit'}…`}
          </div>
          <div className="composer">
            <textarea
              data-testid="saisie"
              value={saisie}
              placeholder={courant ? `Écrire dans # ${courant.name}` : 'Choisissez un canal'}
              onChange={(e) => {
                setSaisie(e.target.value);
                socket.current?.emit('typing', { channel_id: canal });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void envoyer(false); }
              }}
              rows={1}
            />
            <button className="solid" data-testid="envoyer" onClick={() => void envoyer(false)} disabled={!saisie.trim()}>
              Envoyer
            </button>
          </div>
          <div className="aide">
            <kbd>Entrée</kbd> pour envoyer · <kbd>Maj+Entrée</kbd> pour aller à la ligne ·
            {' '}<code>```</code> pour un bloc de code
          </div>
        </div>
      </main>

      <aside className="panel">
        {fil ? (
          <>
            <div className="panel-head">
              <h3>Fil de discussion</h3>
              <button className="fermer" onClick={() => setFil(null)} aria-label="Fermer le fil">×</button>
            </div>
            <div className="stream fil" data-testid="fil-panneau">
              {(() => {
                const racine = messages.find((m) => m.id === fil);
                return racine ? <Bulle m={racine} moi={moi?.id} racine /> : null;
              })()}
              <div className="separateur-fil">
                {reponses.length} réponse{reponses.length > 1 ? 's' : ''}
              </div>
              {reponses.map((r) => (
                <Bulle key={r.id} m={r} moi={moi?.id} />
              ))}
            </div>
            <div className="composer-wrap">
              <div className="composer">
                <textarea
                  data-testid="saisie-fil"
                  value={saisieFil}
                  placeholder="Répondre dans le fil…"
                  onChange={(e) => setSaisieFil(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void envoyer(true); }
                  }}
                  rows={1}
                />
                <button
                  className="solid"
                  data-testid="envoyer-fil"
                  onClick={() => void envoyer(true)}
                  disabled={!saisieFil.trim()}
                >
                  Répondre
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
        <div className="panel-head"><h3>Membres</h3></div>
        <ul className="membres">
          {membres.map((u) => (
            <li key={u.id}>
              <span className={'pres ' + (presences[u.id] ?? 'off')} />
              <span className="av petit" style={{ background: u.avatar_color }}>{initiales(u.display_name)}</span>
              <span>{u.display_name}</span>
            </li>
          ))}
        </ul>
          </>
        )}
      </aside>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────

/** Message affiché dans le panneau de fil, sans les actions du canal. */
function Bulle({ m, moi, racine }: { m: Message; moi?: string; racine?: boolean }) {
  return (
    <article className={'msg' + (racine ? ' racine' : '')} data-testid="message-fil">
      <span className="av" style={{ background: m.avatar_color ?? '#8A8A93' }}>
        {initiales(m.display_name ?? '?')}
      </span>
      <div className="corps">
        <div className="mhead">
          <b>{m.display_name ?? 'Compte supprimé'}</b>
          <time>{heure(m.created_at)}</time>
          {m.en_cours && <span className="en-cours-note">envoi…</span>}
        </div>
        <div className="texte">
          <RendreCorps doc={m.body} moi={moi} />
        </div>
      </div>
    </article>
  );
}

function Marque() {
  return (
    <span className="marque">
      M<i>R</i> <em>Zephyr</em>
    </span>
  );
}

function initiales(nom: string): string {
  return nom.split(' ').map((m) => m[0] ?? '').slice(0, 2).join('').toUpperCase();
}

function heure(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function messageDe(e: unknown): string {
  // Le serveur formule ses erreurs pour être lues : on ne les remplace pas
  // par un message générique.
  if (e instanceof ErreurApi) return e.message;
  return e instanceof Error ? e.message : 'Une erreur est survenue.';
}

function basculerReaction(m: Message, emoji: string): Message {
  const liste = [...(m.reactions ?? [])];
  const i = liste.findIndex((r) => r.emoji === emoji);
  if (i === -1) return { ...m, reactions: [...liste, { emoji, count: 1, me: true }] };
  const r = liste[i]!;
  const suivant = { ...r, count: r.count + (r.me ? -1 : 1), me: !r.me };
  liste[i] = suivant;
  return { ...m, reactions: liste.filter((x) => x.count > 0) };
}

function appliquerReaction(
  m: Message,
  r: { emoji: string; count: number; user_id: string; added: boolean },
  moi?: string,
): Message {
  const liste = (m.reactions ?? []).filter((x) => x.emoji !== r.emoji);
  if (r.count > 0) {
    const existant = (m.reactions ?? []).find((x) => x.emoji === r.emoji);
    liste.push({
      emoji: r.emoji,
      count: r.count,
      me: r.user_id === moi ? r.added : (existant?.me ?? false),
    });
  }
  return { ...m, reactions: liste };
}

export { apercu, type Doc };
